import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { MultiTariffService } from '../multi-tariff.service';
import { Category, CRITERIA_TYPES, Criterion, DAYS, MAX_CRITERIA, MONTHS, Rate, ValidationFlags } from '../multi-tariff.model';

@Component({
	selector: 'app-multi-tariff-categories',
	templateUrl: './categories.component.html',
	styleUrls: ['./categories.component.css']
})
export class CategoriesComponent implements OnInit, OnChanges {

	@Input() categories: Category[] = [];
	@Input() rates: Rate[] = [];
	@Input() currency: number = null;
	@Input() deletedRateIndex: number = null;
	@Input() invalidFields: ValidationFlags = null;
	@Input() readonly = false;

	@Output() categoriesChange = new EventEmitter<Category[]>();

	CRITERIA_TYPES = CRITERIA_TYPES;
	readonly maxCriteria = MAX_CRITERIA;

	criteriaTypeOptions: { label: string, value: number }[] = [];
	dayOptions: { label: string, value: number }[] = [];
	monthOptions: { label: string, value: number }[] = [];
	yearOptions: number[] = [];

	monthsDays: { [month: number]: number[] } = {};

	ratesOptions: { label: string, value: number }[] = [];

	// which occurrence of a weekday each day-of-month criterion type means, for its written details
	readonly dayOfMonthOrder: { [type: number]: string } = {
		[CRITERIA_TYPES.FIRST_DAY_OF_MONTH]: 'first',
		[CRITERIA_TYPES.SECOND_DAY_OF_MONTH]: 'second',
		[CRITERIA_TYPES.THIRD_DAY_OF_MONTH]: 'third',
		[CRITERIA_TYPES.FORTH_DAY_OF_MONTH]: 'fourth',
		[CRITERIA_TYPES.LAST_DAY_OF_MONTH]: 'last'
	};

	selectedCurrency = '';

	constructor(
		private translateService: TranslateService,
		public multiTariffService: MultiTariffService
	) { }

	ngOnInit() {
		this.criteriaTypeOptions = Object.keys(CRITERIA_TYPES).map((type) => ({
			label: this.translateService.instant('multi_tariff.categories.' + type.toLowerCase()),
			value: CRITERIA_TYPES[type]
		}));

		this.dayOptions = Object.keys(DAYS).map((day) => ({
			label: this.translateService.instant('g.' + day.toLowerCase()),
			value: DAYS[day]
		}));

		this.monthOptions = Object.keys(MONTHS).map((month) => ({
			label: this.translateService.instant('g.' + month.toLowerCase()),
			value: MONTHS[month]
		}));

		this.yearOptions = this.multiTariffService.getYears();

		for (const month of Object.values(MONTHS))
			this.monthsDays[month] = this.multiTariffService.getMonthDays(month);
	}

	ngOnChanges(changes: SimpleChanges) {
		this.selectedCurrency = this.multiTariffService.getCurrencyName(this.currency);

		if (changes.deletedRateIndex && this.deletedRateIndex !== null)
			this.repointCategoriesAfterRateRemoval(this.deletedRateIndex);

		this.setRatesOptions();
	}

	setRatesOptions() {
		this.ratesOptions = this.rates
			.map((rate, index) => ({ label: rate.name, value: index }))
			.filter((option) => !!option.label);
	}

	repointCategoriesAfterRateRemoval(removedIndex: number) {
		const categories = cloneDeep(this.categories);

		for (const category of categories) {
			if (category.rate_id === null || category.rate_id === undefined)
				continue;

			if (category.rate_id === removedIndex)
				category.rate_id = null;
			else if (category.rate_id > removedIndex)
				category.rate_id = category.rate_id - 1;
		}

		this.categories = categories;
		this.emit();
	}

	addCategory() {
		const categories = cloneDeep(this.categories);
		categories.push({ name: '', rate_id: null, criteria: [{ name: '', type: null, details: {} }], show: true });

		this.categories = categories;
		this.emit();
	}

	removeCategory(categoryIndex: number) {
		const categories = cloneDeep(this.categories);
		categories.splice(categoryIndex, 1);

		this.categories = categories;
		this.emit();
	}

	moveCategory(categoryIndex: number, step: number) {
		const targetIndex = categoryIndex + step;
		if (targetIndex < 0 || targetIndex >= this.categories.length)
			return;

		const categories = cloneDeep(this.categories);
		categories.splice(targetIndex, 0, categories.splice(categoryIndex, 1)[0]);

		this.categories = categories;
		this.emit();
	}

	addCriterion(categoryIndex: number) {
		const categories = cloneDeep(this.categories);
		categories[categoryIndex].criteria.push({ name: '', type: null, details: {} });

		this.categories = categories;
		this.emit();
	}

	removeCriterion(categoryIndex: number, criteriaIndex: number) {
		const categories = cloneDeep(this.categories);
		categories[categoryIndex].criteria.splice(criteriaIndex, 1);

		this.categories = categories;
		this.emit();
	}

	onCriteriaTypeChange(categoryIndex: number, criteriaIndex: number) {
		const categories = cloneDeep(this.categories);
		const criterion = categories[categoryIndex].criteria[criteriaIndex];

		switch (criterion.type) {
			case CRITERIA_TYPES.PERIOD_OF_YEAR:
				criterion.details = { from: {}, to: {} };
				break;
			case CRITERIA_TYPES.DAYS_OF_WEEK:
				criterion.details = { days: [] };
				break;
			case CRITERIA_TYPES.FIXED_DATE:
				criterion.details = { date: {} };
				break;
			default:
				criterion.details = {};
				break;
		}

		this.categories = categories;
		this.emit();
	}

	resetDayForMonth(categoryIndex: number, criteriaIndex: number, source: string) {
		const categories = cloneDeep(this.categories);
		const details = categories[categoryIndex].criteria[criteriaIndex].details;

		if (source && details[source])
			details[source].day = null;

		this.categories = categories;
		this.emit();
	}

	criterionDetailsText(criterion: Criterion): string {
		const details = criterion.details || {};

		switch (criterion.type) {
			case CRITERIA_TYPES.PERIOD_OF_YEAR:
				if (!details.from?.month || !details.from?.day || !details.to?.month || !details.to?.day)
					return '';

				return this.translateService.instant('multi_tariff.categories.details_period', {
					from: this.dateText(details.from),
					to: this.dateText(details.to)
				});
			case CRITERIA_TYPES.DAYS_OF_WEEK:
				return (details.days || []).map((day: number) => this.optionLabel(this.dayOptions, day)).join(', ');
			case CRITERIA_TYPES.FIXED_DATE:
				if (!details.date?.month || !details.date?.day)
					return '';

				return this.dateText(details.date);
			case CRITERIA_TYPES.YEAR:
				return details.year ? String(details.year) : '';
			default: {
				const order = this.dayOfMonthOrder[criterion.type];
				if (!order || !details.day || !details.month)
					return '';

				return this.translateService.instant('multi_tariff.categories.details_day_of_month', {
					order: this.translateService.instant('multi_tariff.categories.' + order),
					day: this.optionLabel(this.dayOptions, details.day),
					month: this.optionLabel(this.monthOptions, details.month)
				});
			}
		}
	}

	private dateText(date: { month: number, day: number }): string {
		return date.day + '-' + this.optionLabel(this.monthOptions, date.month);
	}

	private optionLabel(options: { label: string, value: number }[], value: number): string {
		const option = options.find((item) => item.value === value);
		return option ? option.label : '';
	}

	toggleCategory(categoryIndex: number) {
		this.categories[categoryIndex].show = !this.categories[categoryIndex].show;
	}

	categoryError(categoryIndex: number, field: string) {
		const categoryErrors = this.invalidFields && this.invalidFields.categories && this.invalidFields.categories[categoryIndex];
		return categoryErrors ? categoryErrors[field] : null;
	}

	criterionError(categoryIndex: number, criteriaIndex: number, field: string) {
		const criteriaErrors = this.categoryError(categoryIndex, 'criteria');
		const criterionErrors = criteriaErrors && criteriaErrors[criteriaIndex];
		return criterionErrors ? criterionErrors[field] : null;
	}

	detailError(categoryIndex: number, criteriaIndex: number, path: string[]) {
		let node = this.criterionError(categoryIndex, criteriaIndex, 'details');

		for (const key of path) {
			if (!node || typeof node !== 'object')
				return null;

			node = node[key];
		}

		return node === undefined ? null : node;
	}

	emit() {
		this.categoriesChange.emit(this.categories);
	}
}
